import { ApiSignup, refreshAccessToken } from './api-signup'
import { ApiHCA } from './api-hca'
import { ApiRanking } from './api-ranking'
import { ApiWidget } from './api-widget'
import Axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { isProduction } from '../interfaces/constants'
import { ApiSocket } from './api-socket'
import { ApiEgg } from './api-egg'
import { decodeToken } from '../helpers/validators'
import DayJS from '../helpers/DayJS'
import { ApiPresent } from './api-present'
import { ApiContent } from './api-content'
import { isPublicRoute } from '../router/AppRoutes'

export const ERR_500 = 'An Error has occurred, and we are working to fix the problem!'

export const setNewAccessToken = (token: null | string) => {
    if (!token || !verifyToken(token)) return removeToken()
    if (`Bearer ${token}` === ApiSignup.defaults.headers.common.Authorization) return

    if (!isProduction) {
        const exp = decodeToken<{ exp: number }>(token)?.exp
        const expIn = exp
            ? DayJS(exp * 1000)
                  .locale('en')
                  .fromNow(true)
            : ''
        console.info(`Access token refreshed. New token expire in ${expIn}.`, `https://jwt.io/#debugger-io?token=${token}`)
    }

    ApiPresent.defaults.headers.common.Authorization = `Bearer ${token}`
    ApiContent.defaults.headers.common.Authorization = `Bearer ${token}`
    ApiSignup.defaults.headers.common.Authorization = `Bearer ${token}`
    ApiRanking.defaults.headers.common.Authorization = `Bearer ${token}`
    ApiHCA.defaults.headers.common.Authorization = `Bearer ${token}`
    ApiSocket.defaults.headers.common.Authorization = `Bearer ${token}`
    ApiWidget.defaults.headers.common.Authorization = `${token}`
    Axios.defaults.headers.common.Authorization = `${token}`
    ApiEgg.defaults.headers.common['access-token'] = `${token}`

    window.dispatchEvent(new StorageEvent('storage', { key: 'token', newValue: token }))
    localStorage.setItem('token', JSON.stringify(token))
    localStorage.setItem('lxp-token', JSON.stringify(token))
}

export const verifyToken = (token?: unknown): boolean => {
    try {
        if (typeof token !== 'string') return false
        const payload = decodeToken<{ _id: string; exp: number }>(token)
        if (!payload || !payload._id) return false
        const currentTime = Math.floor(Date.now() / 1000)
        if (payload.exp && currentTime >= payload.exp) return false
        return true
    } catch {
        return false
    }
}

let isRefreshing = false
const refreshAndRetryQueue: {
    instance: AxiosInstance
    resolve: (value?: any) => void
    reject: (error?: any) => void
    config: AxiosRequestConfig
}[] = []

export const refreshInterceptor = async (error: any, instance: AxiosInstance) => {
    const originalRequest: AxiosRequestConfig = error.config
    if (error.response && error.response.status === 401) {
        const url = error.response.config.url
        const method = error.response.config.method
        if (url === '/v1/auth' && method === 'delete') return Promise.reject(error)
        if (url === '/v1/auth/credentials') return Promise.reject(error)
        if (url === '/v1/auth' && method === 'post') return Promise.reject(error)
        if (url === '/v1/users/who-am-i') return Promise.reject(error)

        if (!isRefreshing) {
            isRefreshing = true
            try {
                // Refreshing the token
                const newAccessToken = await refreshAccessToken()
                console.log(`Access token refreshed. New token: ${newAccessToken}`)
                error.config.headers['Authorization'] = `Bearer ${newAccessToken}`
                setNewAccessToken(newAccessToken)

                refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
                    ApiSignup.request(config)
                        .then(response => resolve(response))
                        .catch(err => reject(err))
                })
                refreshAndRetryQueue.length = 0
                return ApiSignup(originalRequest)
            } catch (refreshError) {
                // Handle refresh token errors
                console.log(`Authorization failed, redirecting to /logout`)
                logoutWithRedirect()
                throw refreshError
            } finally {
                // Reset the isRefreshing flag
                isRefreshing = false
            }
        }
        // If the request is already being refreshed, put it in the queue
        return new Promise<void>((resolve, reject) => {
            refreshAndRetryQueue.push({
                instance,
                resolve,
                reject,
                config: originalRequest,
            })
        })
    }
    return Promise.reject(error)
}

const removeToken = () => {
    delete (ApiPresent.defaults.headers.common as Record<string, unknown>).Authorization
    delete (ApiContent.defaults.headers.common as Record<string, unknown>).Authorization
    delete (ApiSignup.defaults.headers.common as Record<string, unknown>).Authorization
    delete (ApiRanking.defaults.headers.common as Record<string, unknown>).Authorization
    delete (ApiWidget.defaults.headers.common as Record<string, unknown>).Authorization
    delete (ApiHCA.defaults.headers.common as Record<string, unknown>).Authorization
    delete (ApiSocket.defaults.headers.common as Record<string, unknown>).Authorization
    delete (Axios.defaults.headers.common as Record<string, unknown>).Authorization
    delete (ApiEgg.defaults.headers.common as Record<string, unknown>)['access-token']
    localStorage.removeItem('token')
    localStorage.removeItem('lxp-token')
    logoutWithRedirect()
}

const logoutWithRedirect = () => {
    if (isPublicRoute()) return
    let url = '/login'
    const pathname = window.location.pathname
    url += `?redirect=${pathname}`
    console.log(`Authorization failed, redirecting to ${url}`)
    window.location.href = url
}

window.addEventListener('DOMContentLoaded', () => {
    const token = localStorage.getItem('token')
    token && setNewAccessToken(JSON.parse(token))
})
